import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import styles from "../styles/CreateAccount.module.scss";
// components
import CreateAccountCard from "../components/CreateAccount/CreateAccountCard";
import AccountSettingsCard from "../components/CreateAccount/AccountSettingsCard";
import {
  getQueryParamsFromUrl,
  encodeQueryParamsForUrl,
} from "../utils/helpers";
import { MessageTile, ChatSupport } from "@suralink/samurai/ui";
import { Error } from "@suralink/samurai/icons";
import { SuraLogoWithText } from "@suralink/samurai/visuals";
import VerifyPassword from "../components/shared/VerifyPassword";
import * as actions from "../redux/actions";
import { useFlag } from "../utils/gitlab-feature-flags";
import { FeatureFlags } from "../enums/featureFlags";
import ChatIcon from "../icons/Chat";

interface ComponentProps {}

interface StateProps {
  userErrState: boolean;
  onVerifyStep: boolean;
  createAccountInfo: any;
}

interface DispatchProps {
  getCreateAccountInfoAction: (inviteId: string) => any;
}

type Props = StateProps & DispatchProps & ComponentProps & RouteComponentProps;

const CreateAccount: React.FC<Props> = ({
  userErrState,
  onVerifyStep,
  createAccountInfo,
  history,
  getCreateAccountInfoAction,
}) => {
  const { inviteId } = getQueryParamsFromUrl(window.location.href);
  const flagZendesk = useFlag(FeatureFlags.ZendeskChat);

  useEffect(() => {
    getCreateAccountInfoAction(inviteId);
  }, [getCreateAccountInfoAction, inviteId]);

  //If we fail to grab the createAccountInfo in the action or the inviteId is null, user will be redirected to home page.
  useEffect(() => {
    if (!createAccountInfo || !inviteId) {
      window.location.href = "https://www.suralink.com";
    }
  }, [createAccountInfo, inviteId]);

  const [throwUserErr, setThrowUserErr] = useState(false);
  const [userErrMsg, setUserErrMsg] = useState("");

  // TODO: in theory these might come in as enums, so we could pass along the enum maybe and the
  // account settings card could be responsible for translating it as needed, and at this level we
  // would just see the enum and send up the enums to the server when we create the account
  const [timezoneSetting, setTimezoneSetting] = useState({
    value: 26,
    label: "(GMT-07:00) America/Denver",
  });
  const [dateFormatSetting, setDateFormatSetting] = useState({
    value: 1,
    label: "MM/DD/YYYY",
  });
  const [timeFormatSetting, setTimeFormatSetting] = useState({
    value: 0,
    label: "01:20 PM, HH:MM AM/PM",
  });

  const handleOpenChat = () => {
    if (flagZendesk) {
      // @ts-expect-error
      zE("webWidget", "show");
      // @ts-expect-error
      zE("webWidget", "open");
    } else {
      const chatUrl = `https://v2.zopim.com/widget/livechat.html?key=2AekYJAlcSZCOOwpwwxkRyIkGeHIDfty&api_calls=[["concierge.setName",["Suralink Support"]],["concierge.setTitle",["Suralink Support"]],["theme.reload",[]],["theme.reload",[]]]&hostname=${window.location.href}&lang=en`;
      window.open(
        chatUrl,
        "_blank",
        "location=yes,height=570,width=440,scrollbar=yes,status=yes"
      );
    }
  };

  useEffect(() => {
    if (onVerifyStep) {
      let userInfo = {
        inviteId,
      };
      let encodedUserInfo = encodeQueryParamsForUrl(userInfo);
      history.push(`/create-account/verify?${encodedUserInfo}`);
    }
  }, [inviteId, onVerifyStep, history]);

  return (
    <div>
      <div className={styles.createAccountPage}>
        <div>
          {throwUserErr || userErrState ? (
            <MessageTile
              state="error"
              message={
                userErrMsg ||
                "An unexpected error occured. Please contact your firm administrator."
              }
              style={{
                width: "819px",
                margin: "32px 0 0 8px",
              }}
              icon={<Error />}
              dismissAlertCb={() => {
                setThrowUserErr(false);
              }}
            />
          ) : null}
          <div
            className={styles.createForm}
            style={{
              padding: `${throwUserErr ? "24px 0 0 0" : "40px 0 0 0"}`,
            }}
          >
            <CreateAccountCard
              throwUserErr={setThrowUserErr}
              setUserErrMsg={setUserErrMsg}
              inviteId={inviteId}
              dateFormat={dateFormatSetting}
              timeFormat={timeFormatSetting}
              timeZone={timezoneSetting}
            />
            <div className={styles.accountExtra}>
              <div
                style={{
                  paddingLeft: "107px",
                  margin: "0 0 32px 0",
                }}
              >
                {createAccountInfo.firmLogo && (
                  <SuraLogoWithText width={146} height={32} />
                )}
              </div>
              <AccountSettingsCard
                timezoneSetting={timezoneSetting}
                dateFormatSetting={dateFormatSetting}
                timeFormatSetting={timeFormatSetting}
                setTimezoneSetting={setTimezoneSetting}
                setDateFormatSetting={setDateFormatSetting}
                setTimeFormatSetting={setTimeFormatSetting}
              />
              <VerifyPassword />
            </div>
          </div>
          {flagZendesk ? (
            <div
              className={styles.btn}
              style={{
                position: "relative",
              }}
              onClick={handleOpenChat}
            >
              Live chat support
              <span
                style={{
                  position: "relative",
                  left: "8px",
                  top: "1px",
                }}
              >
                <ChatIcon fillColor="#2a5a8c" />
              </span>
            </div>
          ) : (
            <ChatSupport style={{ margin: "24px auto" }} />
          )}
        </div>
      </div>
    </div>
  );
};

const mstp = (state: InitialStateModel): StateProps => {
  const { userErrState, onVerifyStep, createAccountInfo } = state;
  return {
    userErrState,
    onVerifyStep,
    createAccountInfo,
  };
};

const matp = ((): DispatchProps => {
  const { getCreateAccountInfoAction } = actions;
  return {
    getCreateAccountInfoAction,
  };
})();

export default connect(mstp, matp)(CreateAccount);
